import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Envelope, PaperPlaneTilt, CircleNotch, ShieldCheck, Info, Warning, ArrowClockwise } from '@phosphor-icons/react';

const SettingsPage = () => {
  const [form, setForm] = useState({
    host: 'smtps.aruba.it', port: 465, username: '', from_address: '',
    from_name: 'VivaLink Italia', enabled: true, password: '',
  });
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const fetchLogs = async () => {
    setLoadingLogs(true);
    try {
      const r = await api.get('/api/admin/settings/email/logs');
      setLogs(r.data?.logs || []);
    } catch { /* silent */ } finally { setLoadingLogs(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/api/admin/settings/email');
        const d = r.data || {};
        setForm((f) => ({
          ...f,
          host: d.host || 'smtps.aruba.it',
          port: d.port || 465,
          username: d.username || '',
          from_address: d.from_address || '',
          from_name: d.from_name || 'VivaLink Italia',
          enabled: !!d.enabled,
          password: '',
        }));
        setConfigured(!!d.configured);
      } catch { /* silent */ } finally { setLoading(false); }
    })();
    fetchLogs();
  }, []);

  const change = (k) => (e) => { const v = e.target.value; setForm((prev) => ({ ...prev, [k]: v })); };

  const save = async (e) => {
    e.preventDefault();
    if (!configured && !form.password) {
      toast.error('Inserisci la password SMTP alla prima configurazione.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      const r = await api.put('/api/admin/settings/email', payload);
      setConfigured(!!r.data?.configured);
      setForm((f) => ({ ...f, password: '' }));
      toast.success('Impostazioni email salvate.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore nel salvataggio.');
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo) { toast.error('Inserisci un indirizzo email per il test.'); return; }
    setTesting(true);
    try {
      const r = await api.post('/api/admin/settings/email/test', { recipient: testTo });
      if (r.data?.success) {
        toast.success(r.data?.message || 'Email di test inviata.');
      } else {
        toast.error(r.data?.message || 'Invio del test non riuscito.', { duration: 10000 });
      }
      fetchLogs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invio del test non riuscito.');
      fetchLogs();
    } finally { setTesting(false); }
  };

  const senderMismatch = form.from_address && form.username &&
    form.from_address.trim().toLowerCase() !== form.username.trim().toLowerCase();

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
        <CircleNotch size={18} className="animate-spin" /> Caricamento…
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-8" data-testid="settings-page">
      <div className="mb-6">
        <h2 className="font-heading font-bold text-2xl text-foreground">Impostazioni Email</h2>
        <p className="text-sm text-slate-500 mt-1">Configura l'invio email tramite il tuo account Aruba (SMTP). Le credenziali sono salvate in modo cifrato e non vengono mai mostrate.</p>
      </div>

      <div className="flex items-start gap-2 bg-secondary/60 border border-[#E3F2FD] rounded-xl p-3 mb-5 text-sm text-slate-600" data-testid="settings-aruba-hint">
        <Info size={18} className="text-primary shrink-0 mt-0.5" />
        <span>Parametri Aruba: host <strong>smtps.aruba.it</strong>, porta <strong>465 (SSL)</strong> oppure <strong>587 (STARTTLS)</strong>. Username = indirizzo email completo. Il mittente deve coincidere con la casella autenticata.</span>
      </div>

      <Card className="rounded-2xl p-6">
        <form onSubmit={save} className="space-y-5">
          <div className="flex items-center justify-between p-3 rounded-xl bg-[#F5FAFF] border border-[#E3F2FD]">
            <div>
              <div className="font-medium text-foreground flex items-center gap-2"><Envelope size={18} className="text-primary" /> Invio email attivo</div>
              <div className="text-xs text-slate-500">Se disattivato, le email vengono solo registrate (non inviate).</div>
            </div>
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm((prev) => ({ ...prev, enabled: v }))} data-testid="settings-enabled-switch" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Host SMTP</Label>
              <Input value={form.host} onChange={change('host')} data-testid="settings-host" />
            </div>
            <div>
              <Label>Porta</Label>
              <div className="flex gap-2 mt-2">
                {[465, 587].map((p) => (
                  <button
                    type="button"
                    key={p}
                    onClick={() => setForm((prev) => ({ ...prev, port: p }))}
                    data-testid={`settings-port-${p}`}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-colors ${form.port === p ? 'bg-primary text-white border-primary' : 'bg-white text-slate-600 border-[#E3F2FD] hover:border-primary/40'}`}
                  >
                    {p} {p === 465 ? '· SSL' : '· STARTTLS'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <Label>Username (email completa Aruba)</Label>
            <Input type="email" value={form.username} onChange={change('username')} placeholder="es. info@tuodominio.it" data-testid="settings-username" required />
          </div>

          <div>
            <Label>Password casella
              {configured && <span className="ml-2 text-xs text-emerald-600 inline-flex items-center gap-1"><ShieldCheck size={14} /> già impostata</span>}
            </Label>
            <Input type="password" value={form.password} onChange={change('password')} placeholder={configured ? 'Lascia vuoto per non modificarla' : 'Inserisci la password'} data-testid="settings-password" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Indirizzo mittente (From)</Label>
              <Input type="email" value={form.from_address} onChange={change('from_address')} placeholder="es. info@tuodominio.it" data-testid="settings-from-address" required />
            </div>
            <div>
              <Label>Nome mittente</Label>
              <Input value={form.from_name} onChange={change('from_name')} data-testid="settings-from-name" />
            </div>
          </div>

          {senderMismatch && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800" data-testid="settings-sender-warning">
              <Warning size={18} className="shrink-0 mt-0.5" />
              <span>Il mittente (<strong>{form.from_address}</strong>) è diverso dalla casella autenticata (<strong>{form.username}</strong>). Aruba potrebbe rifiutare l'invio con "Sender not allowed" se questo indirizzo non è autorizzato nella Webmail.</span>
            </div>
          )}

          <Button type="submit" disabled={saving} className="rounded-full" data-testid="settings-save-btn">
            {saving ? <><CircleNotch size={16} className="animate-spin mr-2" /> Salvataggio…</> : 'Salva impostazioni'}
          </Button>
        </form>
      </Card>

      <Card className="rounded-2xl p-6 mt-5">
        <div className="font-medium text-foreground mb-1">Invia un'email di test</div>
        <p className="text-xs text-slate-500 mb-3">Verifica che le credenziali funzionino inviando un'email di prova. Salva prima le impostazioni.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="destinatario@esempio.it" data-testid="settings-test-recipient" />
          <Button type="button" variant="secondary" onClick={sendTest} disabled={testing} className="rounded-full shrink-0" data-testid="settings-test-btn">
            {testing ? <><CircleNotch size={16} className="animate-spin mr-2" /> Invio…</> : <><PaperPlaneTilt size={16} className="mr-2" /> Invia test</>}
          </Button>
        </div>
      </Card>

      <Card className="rounded-2xl p-6 mt-5" data-testid="settings-logs-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-medium text-foreground">Registro invii</div>
            <p className="text-xs text-slate-500">Ultimi invii email con esito.</p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={fetchLogs} className="rounded-full" data-testid="settings-logs-refresh">
            <ArrowClockwise size={16} className={loadingLogs ? 'animate-spin' : ''} />
          </Button>
        </div>
        {logs.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center" data-testid="settings-logs-empty">Nessun invio registrato.</p>
        ) : (
          <div className="divide-y divide-[#E3F2FD]" data-testid="settings-logs-list">
            {logs.map((l, i) => {
              const map = { sent: ['Inviata', 'text-emerald-600 bg-emerald-50'], error: ['Errore', 'text-red-600 bg-red-50'], suppressed: ['Non inviata', 'text-slate-500 bg-slate-100'] };
              const [label, cls] = map[l.status] || [l.status, 'text-slate-500 bg-slate-100'];
              return (
                <div key={i} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`settings-log-${i}`}>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${cls}`}>{label}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-foreground">{l.subject}</div>
                    <div className="text-xs text-slate-400">{l.recipient_masked}{l.error ? ` · ${l.error}` : ''}</div>
                  </div>
                  <span className="text-[11px] text-slate-400 shrink-0">
                    {l.created_at ? new Date(l.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default SettingsPage;
